"use client";

import React from "react";
import { Phone, Clock, ShieldCheck, ArrowRight, Zap, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const features = [{
  id: 1,
  title: "24/7 Rapid Response",
  description: "Plumbers ready for urgent heating failures."
}, {
  id: 2,
  title: "Licensed & Insured",
  description: "Expert diagnostics on all electric tank and hybrid models."
}, {
  id: 3,
  title: "Upfront Pricing",
  description: "Clear repair or replacement estimates before work starts."
}];
export default function ElectricWaterHeaterCta() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="electricwaterheatercta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="relative rounded-2xl bg-primary text-primary-foreground p-8 md:p-12 lg:p-16 shadow-xl overflow-hidden">
          {/* Decorative background ambient glows */}
          <div className="absolute top-0 right-0 -mt-16 -mr-16 size-80 rounded-full bg-accent/15 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -mb-16 -ml-16 size-72 rounded-full bg-chart-3/15 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-6">
              <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="bg-accent text-accent-foreground border-accent font-semibold px-3 py-1 text-xs uppercase tracking-wide gap-1.5">
                  <Zap className="size-3.5 fill-current" />
                  Emergency Water Heater Service
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-xs text-primary-foreground/80 font-medium">
                  <Clock className="size-3.5 text-accent" />
                  Average Response: Under 45 Mins in Jonesboro
                </span>
              </motion.div>

              <motion.div variants={itemVariants} className="space-y-3">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-primary-foreground font-default">
                  Need Fast Electric Water Heater Repair or Replacement?
                </h2>
                <p className="text-base sm:text-lg text-primary-foreground/90 max-w-2xl leading-relaxed">
                  No hot water or dealing with a leaking tank? Our certified technicians provide prompt 24/7 diagnostics, element replacements, thermostat fixes, and complete installations in Jonesboro and surrounding areas.
                </p>
              </motion.div>

              {/* Feature Points */}
              <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                {features.map((feature, i) => <div key={feature.id} data-index={i} className="flex flex-col gap-1 rounded-lg bg-primary-foreground/5 p-3 border border-primary-foreground/10">
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-accent">
                      <CheckCircle2 className="size-4 shrink-0" />
                      <span>{feature.title}</span>
                    </div>
                    <p className="text-xs text-primary-foreground/80 leading-normal">
                      {feature.description}
                    </p>
                  </div>)}
              </motion.div>
            </div>

            {/* Right Action Box */}
            <motion.div variants={itemVariants} className="lg:col-span-5 flex flex-col justify-center items-center lg:items-stretch">
              <div className="w-full max-w-md rounded-xl bg-card text-card-foreground p-6 sm:p-8 shadow-lg border border-border/80 flex flex-col gap-6">
                <div className="text-center space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Direct Dispatch Line
                  </span>
                  <p className="text-xs text-muted-foreground">
                    Speak directly with an on-call water heater specialist
                  </p>
                </div>

                {/* Prominent Phone Box with Accent Styling */}
                <a href="tel:4708479076" className="group flex flex-col items-center justify-center gap-1 rounded-xl bg-accent p-4 text-accent-foreground font-bold shadow-md hover:brightness-105 active:scale-[0.99] transition-all" aria-label="Call (470) 847-9076 for emergency service">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                    <Phone className="size-4 animate-pulse" />
                    <span>Call 24/7 Available</span>
                  </div>
                  <span className="text-2xl sm:text-3xl tracking-tight font-extrabold">
                    (470) 847-9076
                  </span>
                </a>

                <div className="flex flex-col gap-3">
                  <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm">
                    <Link to="/contact/" className="flex items-center justify-center gap-2">
                      <span>Request Service</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-1">
                    <ShieldCheck className="size-4 text-primary" />
                    <span>Zero hassle scheduling • Jonesboro, GA</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}